import React, { useState, useEffect, useRef } from "react";
import {
  FiShoppingCart,
  FiSearch,
  FiChevronDown,
  FiUser,
  FiCreditCard,
  FiGift,
  FiLogOut,
  FiBox,
  FiKey,
} from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import "./components.css";
import logo from "../assets/Images/logo.png";
import { Link, useNavigate } from "react-router-dom";

import { HiMenuAlt3 } from "react-icons/hi";
import { getCartItems } from "../util/Cart";

const Navbar = () => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [profileMenuVisible, setProfileMenuVisible] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const dropdownRef = useRef(null);
  const profileMenuRef = useRef(null);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");

  const categories = [
    "Electronics",
    "Toys",
    "Hardware",
    "Personal Care",
    "Household Appliances",
    "Clothing Accessories",
  ];

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchClick = () => {
    if (searchInput.trim()) {
      navigate(`/products?keyword=${searchInput}`);
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const toggleProfileMenu = () => {
    setProfileMenuVisible(!profileMenuVisible);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownVisible(false);
    }
    if (
      profileMenuRef.current &&
      !profileMenuRef.current.contains(event.target)
    ) {
      setProfileMenuVisible(false);
    }
  };

  const handleLinkClick = () => {
    setDropdownVisible(false);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      setUserData(JSON.parse(storedUser));
    }

    const handleStorageChange = () => {
      const updatedUser = localStorage.getItem("userData");
      if (updatedUser) {
        setUserData(JSON.parse(updatedUser));
      } else {
        setUserData(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userDataUpdated", handleStorageChange);

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userDataUpdated", handleStorageChange);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userData");
    setUserData(null);
    navigate("/login");
  };

  useEffect(() => {
    const updateCartItemCount = () => {
      const cartItems = getCartItems();
      const itemCount = cartItems.length;
      setCartItemCount(itemCount);
    };

    updateCartItemCount();
    window.addEventListener("storage", updateCartItemCount);

    return () => {
      window.removeEventListener("storage", updateCartItemCount);
    };
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="top-bar">
          <div className="top-bar-content">
            <div className="top-bar-left">
              <span className="welcome-text">
                {userData?.user?.name ? (
                  <>
                    Welcome back, <span className="user-name-highlight">{userData.user.name.split(" ")[0]}</span>!
                  </>
                ) : (
                  <>
                    <Link className="auth-link" to="./login">
                      Sign In
                    </Link>
                    <span className="divider">|</span>
                    <Link className="auth-link" to="/register">
                      Register
                    </Link>
                  </>
                )}
              </span>
            </div>
            <div className="top-bar-right">
              <Link className="top-link" to="/">
                Daily Deals
              </Link>
              <Link className="top-link" to="/">
                Help & Contact
              </Link>
            </div>
          </div>
        </div>

        <div className="main-navbar">
          <div className="main-navbar-content">
            <Link to="/" className="brand-logo">
              <img src={logo} alt="CGT Homes Logo" />
            </Link>

            <div className="nav-center">
              <div
                className="category-dropdown"
                ref={dropdownRef}
              >
                <button className="category-btn" onClick={toggleDropdown}>
                  <span>Categories</span>
                  <FiChevronDown className={`dropdown-icon${dropdownVisible ? ' open' : ''}`} />
                </button>
                {dropdownVisible && (
                  <div className="dropdown-menu">
                    {categories.map((category) => (
                      <Link
                        key={category}
                        to={`/products?productCategory=${encodeURIComponent(category)}`}
                        onClick={() => setDropdownVisible(false)}
                        className="dropdown-item"
                      >
                        {category}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="search-input"
                />
                <button className="search-btn" onClick={handleSearchClick}>
                  <FiSearch />
                </button>
              </div>
            </div>

            <div className="nav-actions">
              <Link to="/cart" className="cart-btn">
                <FiShoppingCart className="icon" />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </Link>

              {userData && (
                <div
                  className="profile-dropdown"
                  ref={profileMenuRef}
                  onClick={toggleProfileMenu}
                >
                  <button className="profile-btn">
                    {profileMenuVisible ? <IoClose className="icon" /> : <HiMenuAlt3 className="icon" />}
                  </button>
                  {profileMenuVisible && (
                    <div className="profile-dropdown-menu">
                      <div className="dropdown-header">
                        <p className="dropdown-user-name">{userData?.user?.name}</p>
                        <p className="dropdown-user-email">{userData?.user?.email}</p>
                      </div>
                      <div className="dropdown-divider"></div>
                      <Link to="/profile" className="dropdown-menu-item">
                        <FiUser className="menu-icon" />
                        <span>My Profile</span>
                      </Link>
                      <Link to="/wallet" className="dropdown-menu-item">
                        <FiCreditCard className="menu-icon" />
                        <span>Wallet</span>
                      </Link>
                      <Link to="/reward" className="dropdown-menu-item">
                        <FiGift className="menu-icon" />
                        <span>Refer & Earn</span>
                      </Link>
                      <Link to="/orders" className="dropdown-menu-item">
                        <FiBox className="menu-icon" />
                        <span>My Orders</span>
                      </Link>
                      <Link to="/hybrid-pins" className="dropdown-menu-item">
                        <FiKey className="menu-icon" />
                        <span>Hybrid Pins</span>
                      </Link>
                      <div className="dropdown-divider"></div>
                      <div className="dropdown-menu-item logout-item" onClick={handleLogout}>
                        <FiLogOut className="menu-icon" />
                        <span>Logout</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
